/**
 * Reporting orchestration service
 */

import { supabase, getCurrentUserId } from '../../lib/supabase';
import type { Json } from '../../lib/database.types';
import { formatDate } from '../../lib/regions/au';
import { resolveReportDateRange } from '../../lib/regions/au';
import { buildReportDisplayTitle } from '../../utils/report-title';
import { reportGenerationInputSchema, exportPreferencesSchema } from '../../utils/report-validation';
import { buildDataset } from './report-data-service';
import { renderReport } from './report-export-service';
import {
  DEFAULT_REPORTING_USER_CONTEXT,
  loadReportingUserContext,
} from './reporting-user-context';
import {
  uploadReportFile,
  getSignedDownloadUrl,
  deleteReportFile,
} from './report-storage-service';
import type {
  ExportPreferences,
  GenerateReportResult,
  QuickExportResult,
  ReportFormat,
  ReportGenerationInput,
  ReportListResponse,
  ReportType,
  DownloadUrlResult,
  ServiceResponse,
} from '../../types/report.types';

const DEFAULT_EXPORT_PREFS: ExportPreferences = {
  defaultFormat: 'pdf',
  defaultDatePreset: 'this_month',
  customStartDate: null,
  customEndDate: null,
};

function mapListItem(row: Record<string, unknown>) {
  return {
    id: row.id as string,
    title: row.title as string,
    reportType: row.report_type as ReportType,
    format: row.format as ReportFormat,
    status: row.status as 'pending' | 'processing' | 'succeeded' | 'failed',
    dateRangeLabel: row.date_range_label as string,
    fileSizeBytes: row.file_size_bytes != null ? Number(row.file_size_bytes) : null,
    createdAt: row.created_at as string,
    createdAtDisplay: formatDate(row.created_at as string),
  };
}

export async function listReports(
  options: { limit?: number; offset?: number; userId?: string } = {}
): Promise<ServiceResponse<ReportListResponse>> {
  try {
    const uid = options.userId ?? (await getCurrentUserId());
    if (!uid) return { data: null, error: 'Not authenticated' };

    const limit = options.limit ?? 5;
    const offset = options.offset ?? 0;

    const { data, error, count } = await supabase
      .from('generated_reports')
      .select('*', { count: 'exact' })
      .eq('user_id', uid)
      .order('created_at', { ascending: false })
      .range(offset, offset + limit - 1);

    if (error) return { data: null, error: error.message };

    return {
      data: {
        items: (data ?? []).map(mapListItem),
        total: count ?? 0,
      },
      error: null,
    };
  } catch (e) {
    return { data: null, error: e instanceof Error ? e.message : 'Failed to list reports' };
  }
}

export async function getExportPreferences(userId?: string): Promise<ServiceResponse<ExportPreferences>> {
  try {
    const uid = userId ?? (await getCurrentUserId());
    if (!uid) return { data: null, error: 'Not authenticated' };

    const { data, error } = await supabase
      .from('user_preferences')
      .select('report_default_format, report_default_date_preset, report_custom_start_date, report_custom_end_date')
      .eq('user_id', uid)
      .maybeSingle();

    if (error) return { data: DEFAULT_EXPORT_PREFS, error: null };

    return {
      data: {
        defaultFormat: (data?.report_default_format as ReportFormat) ?? DEFAULT_EXPORT_PREFS.defaultFormat,
        defaultDatePreset:
          (data?.report_default_date_preset as ExportPreferences['defaultDatePreset']) ??
          DEFAULT_EXPORT_PREFS.defaultDatePreset,
        customStartDate: data?.report_custom_start_date ?? null,
        customEndDate: data?.report_custom_end_date ?? null,
      },
      error: null,
    };
  } catch (e) {
    return { data: null, error: e instanceof Error ? e.message : 'Failed to get export preferences' };
  }
}

export async function updateExportPreferences(
  input: Partial<ExportPreferences>,
  userId?: string
): Promise<ServiceResponse<ExportPreferences>> {
  const parsed = exportPreferencesSchema.safeParse(input);
  if (!parsed.success) {
    return { data: null, error: parsed.error.issues[0]?.message ?? 'Invalid preferences' };
  }

  try {
    const uid = userId ?? (await getCurrentUserId());
    if (!uid) return { data: null, error: 'Not authenticated' };

    const payload: Record<string, unknown> = { updated_at: new Date().toISOString() };
    if (input.defaultFormat !== undefined) payload.report_default_format = input.defaultFormat;
    if (input.defaultDatePreset !== undefined) payload.report_default_date_preset = input.defaultDatePreset;
    if (input.customStartDate !== undefined) payload.report_custom_start_date = input.customStartDate;
    if (input.customEndDate !== undefined) payload.report_custom_end_date = input.customEndDate;

    const { data: updated, error } = await supabase
      .from('user_preferences')
      .update(payload)
      .eq('user_id', uid)
      .select('report_default_format, report_default_date_preset, report_custom_start_date, report_custom_end_date')
      .maybeSingle();

    if (error) return { data: null, error: error.message };

    if (!updated) {
      const { error: insertError } = await supabase.from('user_preferences').insert({
        user_id: uid,
        ...payload,
        created_at: new Date().toISOString(),
      });
      if (insertError) return { data: null, error: insertError.message };
    }

    return getExportPreferences(uid);
  } catch (e) {
    return { data: null, error: e instanceof Error ? e.message : 'Failed to update export preferences' };
  }
}

async function runGeneration(
  input: ReportGenerationInput,
  userId: string
): Promise<GenerateReportResult> {
  const parsed = reportGenerationInputSchema.safeParse(input);
  if (!parsed.success) {
    return { id: '', status: 'failed', errorMessage: parsed.error.issues[0]?.message ?? 'Invalid input' };
  }

  const userContext = (await loadReportingUserContext(userId)) ?? DEFAULT_REPORTING_USER_CONTEXT;

  const period = resolveReportDateRange(
    input.dateRangePreset,
    input.customStartDate,
    input.customEndDate,
    new Date(),
    { financialYearStartMonth: userContext.financialYearStartMonth }
  );

  const includeRawData =
    input.reportType === 'business_expense' ? true : input.includeRawData;

  const { data: row, error: insertError } = await supabase
    .from('generated_reports')
    .insert({
      user_id: userId,
      title: input.title,
      report_type: input.reportType,
      format: input.format,
      status: 'processing',
      date_range_preset: input.dateRangePreset,
      date_range_start: period.startDate,
      date_range_end: period.endDate,
      date_range_label: period.label,
      filters_json: (input.filters ?? {}) as unknown as Json,
      include_charts: input.includeCharts,
      include_raw_data: includeRawData,
    })
    .select('id')
    .single();

  if (insertError || !row) {
    return {
      id: '',
      status: 'failed',
      errorMessage: insertError?.message ?? 'Failed to create report record',
    };
  }

  const reportId = row.id as string;

  try {
    const datasetRes = await buildDataset(
      input.reportType,
      period,
      input.filters,
      input.title,
      includeRawData,
      userId,
      userContext,
      input.format
    );
    if (datasetRes.error || !datasetRes.data) {
      throw new Error(datasetRes.error ?? 'Failed to build dataset');
    }

    const rendered = renderReport(datasetRes.data, input.format, {
      includeCharts: input.includeCharts,
      includeRawData,
    });

    const upload = await uploadReportFile(userId, reportId, input.format, rendered.blob);
    if (upload.error) {
      throw new Error(upload.error);
    }

    const fileSizeBytes = rendered.blob.size;
    await supabase
      .from('generated_reports')
      .update({
        status: 'succeeded',
        storage_path: upload.path,
        file_size_bytes: fileSizeBytes,
        completed_at: new Date().toISOString(),
      })
      .eq('id', reportId)
      .eq('user_id', userId);

    const ext = input.format === 'excel' ? 'xlsx' : input.format;
    const downloadFilename = `${input.title.replace(/\//g, '-')}.${ext}`;
    const signed = await getSignedDownloadUrl(upload.path, 3600, downloadFilename);
    if (!signed.url) {
      throw new Error(signed.error ?? 'Failed to create download URL');
    }

    return {
      id: reportId,
      status: 'succeeded',
      downloadUrl: signed.url,
      fileSizeBytes,
      title: input.title,
      format: input.format,
    };
  } catch (e) {
    const message = e instanceof Error ? e.message : 'Report generation failed';
    await supabase
      .from('generated_reports')
      .update({ status: 'failed', error_message: message, completed_at: new Date().toISOString() })
      .eq('id', reportId)
      .eq('user_id', userId);
    return { id: reportId, status: 'failed', errorMessage: message };
  }
}

export async function generateReport(
  input: ReportGenerationInput,
  userId?: string
): Promise<ServiceResponse<GenerateReportResult>> {
  const uid = userId ?? (await getCurrentUserId());
  if (!uid) return { data: null, error: 'Not authenticated' };
  const result = await runGeneration(input, uid);
  return { data: result, error: result.status === 'failed' ? result.errorMessage : null };
}

export async function quickExport(
  reportType: ReportType,
  userId?: string,
  options?: { requiresFilters?: boolean }
): Promise<ServiceResponse<QuickExportResult>> {
  if (options?.requiresFilters) {
    return {
      data: { openWizard: true, step: 2 },
      error: null,
    };
  }

  const uid = userId ?? (await getCurrentUserId());
  if (!uid) return { data: null, error: 'Not authenticated' };

  const prefsRes = await getExportPreferences(uid);
  if (!prefsRes.data) return { data: null, error: prefsRes.error };

  const userContext = (await loadReportingUserContext(uid)) ?? DEFAULT_REPORTING_USER_CONTEXT;

  const period = resolveReportDateRange(
    prefsRes.data.defaultDatePreset,
    prefsRes.data.customStartDate ?? undefined,
    prefsRes.data.customEndDate ?? undefined,
    new Date(),
    { financialYearStartMonth: userContext.financialYearStartMonth }
  );

  const input: ReportGenerationInput = {
    title: buildReportDisplayTitle(reportType, period),
    reportType,
    format: prefsRes.data.defaultFormat,
    dateRangePreset: prefsRes.data.defaultDatePreset,
    customStartDate: prefsRes.data.customStartDate ?? undefined,
    customEndDate: prefsRes.data.customEndDate ?? undefined,
    includeCharts: true,
    includeRawData: reportType === 'business_expense',
  };

  const result = await runGeneration(input, uid);
  return { data: result, error: result.status === 'failed' ? result.errorMessage : null };
}

export async function getDownloadUrl(
  reportId: string,
  userId?: string
): Promise<ServiceResponse<DownloadUrlResult>> {
  try {
    const uid = userId ?? (await getCurrentUserId());
    if (!uid) return { data: null, error: 'Not authenticated' };

    const { data, error } = await supabase
      .from('generated_reports')
      .select('*')
      .eq('id', reportId)
      .eq('user_id', uid)
      .maybeSingle();

    if (error) return { data: null, error: error.message };
    if (!data) return { data: null, error: 'Report not found' };
    if (data.status !== 'succeeded' || !data.storage_path) {
      return { data: null, error: 'Report not available for download' };
    }

    const ext = data.format === 'excel' ? 'xlsx' : data.format;
    const filename = `${data.title} ${formatDate(data.created_at as string)}.${ext}`.replace(/\//g, '-');
    const signed = await getSignedDownloadUrl(data.storage_path as string, 3600, filename);
    if (!signed.url) return { data: null, error: signed.error ?? 'Failed to sign URL' };

    const expiresAt = new Date(Date.now() + 3600 * 1000).toISOString();

    return {
      data: {
        downloadUrl: signed.url,
        filename,
        expiresAt,
      },
      error: null,
    };
  } catch (e) {
    return { data: null, error: e instanceof Error ? e.message : 'Download failed' };
  }
}

export async function deleteReport(
  reportId: string,
  userId?: string
): Promise<ServiceResponse<{ deleted: boolean; id: string }>> {
  try {
    const uid = userId ?? (await getCurrentUserId());
    if (!uid) return { data: null, error: 'Not authenticated' };

    const { data, error } = await supabase
      .from('generated_reports')
      .select('storage_path')
      .eq('id', reportId)
      .eq('user_id', uid)
      .maybeSingle();

    if (error) return { data: null, error: error.message };
    if (!data) return { data: null, error: 'Report not found' };

    if (data.storage_path) {
      await deleteReportFile(data.storage_path as string);
    }

    const { error: delError } = await supabase
      .from('generated_reports')
      .delete()
      .eq('id', reportId)
      .eq('user_id', uid);

    if (delError) return { data: null, error: delError.message };

    return { data: { deleted: true, id: reportId }, error: null };
  } catch (e) {
    return { data: null, error: e instanceof Error ? e.message : 'Delete failed' };
  }
}

export const reportService = {
  listReports,
  getExportPreferences,
  updateExportPreferences,
  generateReport,
  quickExport,
  getDownloadUrl,
  deleteReport,
};
